/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Shared browser correlation id (LGTM platform onboarding, PR-4).
 *
 * A single stable id for the browser session, shared by BOTH the API client
 * (sent as the `x-correlation-id` request header) and the logger (attached to
 * relayed log events as `correlation_id`). This is what lets a sequence of
 * frontend actions thread together with the backend spans/logs they trigger in
 * Grafana (integration doc §8).
 *
 * Generating a fresh id per request — the previous behaviour — defeated that:
 * every request and every log line got a different id, so nothing correlated.
 * The id is session-scoped (one per page load); `resetCorrelationId()` is
 * available to start a new logical trace at a workflow boundary (e.g. login).
 */

let _correlationId: string | null = null;

/** Return the current session correlation id, creating one on first use. */
export function getCorrelationId(): string {
  if (_correlationId === null) {
    _correlationId = crypto.randomUUID();
  }
  return _correlationId;
}

/** Start a new logical trace (e.g. at a login / major workflow boundary). */
export function resetCorrelationId(): string {
  _correlationId = crypto.randomUUID();
  return _correlationId;
}
